import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import {
    DashboardTile,
    SensorTileContent,
    SensorValueTileContent,
    WeatherTileContent,
} from 'commons/src/models/commonTypeScript';
import PublicDashboardOutdoorTile from './PublicDashboardOutdoorTile';
import PublicDashboardSensorTile from './PublicDashboardSensorTile';
import PublicDashboardSensorValueTile from './PublicDashboardSensorValueTile';

type Props = {
    tiles: DashboardTile[];
    numberOfTilesInWidth: number;
};

const PublicDashboardTilesComponent = ({ tiles, numberOfTilesInWidth }: Props): React.ReactElement => {
    const [reloading, setReloading] = useState(false);

    const reloadComponent = () => {
        // this loads a blank page for 10 ms to let the text rescale when changing width;
        setReloading(true);
        setTimeout(() => setReloading(false), 10);
    };

    useEffect((): (() => void) => {
        window.addEventListener('resize', reloadComponent);
        return (): void => window.removeEventListener('resize', reloadComponent);
    }, []);

    useEffect(() => {
        reloadComponent();
    }, [numberOfTilesInWidth]);

    if (reloading) {
        return <div />;
    }
    const tileComponents = tiles
        .map(({ content, type, width, id }) => {
            switch (type) {
                case 'sensorValue':
                    return (
                        <PublicDashboardSensorValueTile
                            tile={content as SensorValueTileContent}
                            key={id}
                            id={id}
                            numberOfTilesInWidth={numberOfTilesInWidth}
                        />
                    );
                case 'sensor': {
                    return (
                        <PublicDashboardSensorTile
                            content={content as SensorTileContent}
                            width={width}
                            key={id}
                            id={id}
                        />
                    );
                }
                case 'outdoor':
                    return (
                        <PublicDashboardOutdoorTile
                            key={id}
                            {...(content as WeatherTileContent)}
                            id={id}
                            numberOfTilesInWidth={numberOfTilesInWidth}
                        />
                    );
                default:
                    return null;
            }
        })
        .filter(tile => tile !== null);
    return (
        <div
            className={classNames('public-dashboard', {
                'public-dashboard--two-in-width': numberOfTilesInWidth === 2,
                'public-dashboard--three-in-width': numberOfTilesInWidth === 3,
                'public-dashboard--four-in-width': numberOfTilesInWidth === 4,
            })}
        >
            {tileComponents}
        </div>
    );
};

export default PublicDashboardTilesComponent;
